import { Link } from "react-router-dom";
import ContactForm from "@/pages/about/components/ContactForm";
import { useSeo } from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";

const VALUES = [
  { icon: "ri-emotion-happy-line", title: "真实不做作", desc: "每一条视频都来自真实的生活记录，不刻意营造完美人设。" },
  { icon: "ri-road-map-line", title: "多元视角", desc: "从日常、旅行到个人思考，用不同切面记录生活的丰富样貌。" },
  { icon: "ri-shield-star-line", title: "用心分享", desc: "认真对待每一位观众，把踩过的坑和想明白的事坦诚讲出来。" },
];

const NOTICE = [
  "本站视频均为糖心Vlog 原创生活记录，画面为前端演示播放器，实际播放以正式发布内容为准。",
  "站内收藏功能保存在你的本地浏览器中，清除浏览器数据后会同步清空。",
  "搜索、筛选、排序均为前端演示，数据来源于站内预加载内容。",
  "留言表单仅用于收集反馈，请勿提交敏感个人信息。",
];

export default function About() {
  useSeo({
    title: "关于与联系 - 糖心Vlog",
    description:
      "了解糖心Vlog 的创办理念、内容范围与站长故事，并通过留言表单或邮箱与糖心取得联系，分享你的想法与建议。",
    keywords: "糖心Vlog,关于糖心Vlog,站长介绍,联系方式,视频博客,留言",
    path: "/about",
  });

  return (
    <div>
      {/* intro header */}
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="font-medium text-foreground-900">关于与联系</span>
          </nav>
          <h1 className="mt-6 max-w-3xl font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            关于糖心Vlog：把真实的生活，认真地记录下来
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            糖心Vlog 是一个专注真实生活记录与精彩视频分享的个人视频博客。
            这里的内容围绕 <strong className="font-semibold text-foreground-800">日常记录</strong>、
            <strong className="font-semibold text-foreground-800">旅行体验</strong> 与
            <strong className="font-semibold text-foreground-800">个人见解</strong> 展开，
            面向喜欢 Vlog 的年轻群体与粉丝，希望用镜头里的真实瞬间，陪你一起慢下来。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className="flex items-center gap-2 rounded-full bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
              >
                <i className={`${c.icon} text-primary-500`} /> {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        {/* values */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {VALUES.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-2xl text-primary-600">
                <i className={v.icon} />
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
            </div>
          ))}
        </div>

        {/* author */}
        <div className="mt-14 grid gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-[280px_1fr] md:p-10">
          <div className="w-full">
            <div className="h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-72">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20friendly%20portrait%20of%20a%20young%20creator%20holding%20a%20camera%20in%20soft%20natural%20light%2C%20cozy%20neutral%20studio%20background%2C%20warm%20orange%20tones%2C%20clean%20editorial%20photography%2C%20authentic%20and%20approachable%20mood%2C%20high%20detail&width=600&height=600&seq=author-portrait&orientation=squarish"
                alt="糖心Vlog 站长糖心"
                title="糖心 - 糖心Vlog 站长"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                Vlog 创作者
              </span>
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-800">
                生活记录者
              </span>
            </div>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              站长简介：糖心
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              大家好，我是糖心，一个喜欢用镜头记录生活的普通人。三年前，我买下第一台相机，
              开始拍的并不是风景，而是自己每天的早餐、通勤路上的一棵树、下班后一个人的晚餐。
              后来我发现，这些看似平淡的画面，反而最能打动人。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              于是有了糖心Vlog。我希望它是一个不那么着急的地方——
              没有剧本、不追热点，只是诚实地记录，并把这份真实分享给屏幕前的你。
              如果你也在认真生活，那我们大概是一类人。
            </p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-4">
              <div>
                <p className="font-heading text-2xl font-extrabold text-foreground-950">3年</p>
                <p className="text-xs text-foreground-500">持续记录</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-foreground-950">100+</p>
                <p className="text-xs text-foreground-500">已发布视频</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-foreground-950">5个</p>
                <p className="text-xs text-foreground-500">走过城市</p>
              </div>
            </div>
          </div>
        </div>

        {/* contact */}
        <div className="mt-14 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              联系方式
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              无论是内容建议、合作洽谈，还是单纯想聊聊天，都欢迎随时联系我。
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href="mailto:hello@tangxinvlog.com"
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600">
                  <i className="ri-mail-line" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">商务与合作邮箱</span>
                  <span className="block text-sm font-medium text-foreground-950">
                    hello@tangxinvlog.com
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-xl text-accent-600">
                  <i className="ri-map-pin-line" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">内容取景地</span>
                  <span className="block text-sm font-medium text-foreground-950">
                    中国 · 多城市生活记录
                  </span>
                </span>
              </div>
            </div>

            {/* notice */}
            <div className="mt-8 rounded-lg border border-background-200 bg-secondary-100/60 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">使用须知</h3>
              <ul className="mt-3 flex flex-col gap-2.5">
                {NOTICE.map((n) => (
                  <li key={n} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              给我留言
            </h2>
            <p className="mt-3 mb-6 text-sm leading-relaxed text-foreground-600">
              填写下面的表单，你的留言会直接送达糖心。感谢每一份真诚的反馈。
            </p>
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
  );
}